import React, { useState, useMemo } from "react";
import _ from "lodash";
import moment from "moment";

import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";

import { useI18n } from "Hooks";
import {
  getClassSettingsFooterText,
  getMomentFromDateAndTimeString,
  checkIsDueDateBeforeOpenDate,
  checkIsCloseDateBeforeDueDate,
  checkIsCloseDateBeforeOpenDate,
  getOpensOnBasedOnTimelineSettings,
  getDueOnBasedOnTimelineSettings,
  getClosesOnBasedOnTimelineSettings,
} from "../../Utils";
import { checkIsDateBefore } from "Utils/DateTimeHelpers";

import classes from "./AssignClassSettingsDataGridFooter.scss";

const AssignClassSettingsDataGridFooter = ({
  courses,
  isFixedStartTime,
  currentTimelineSettings,
  assignmentData,
  isDraftMode,
  timedTestDuration,
}) => {
  const { t } = useI18n();

  // State to track current class index
  const [currentClassIndex, setCurrentClassIndex] = useState(0);

  const currentCourse = courses[currentClassIndex];

  // Calculate footer text for the current course
  const footerText = useMemo(() => {
    if (!currentCourse) return "";

    const {
      deadlineDate,
      deadlineTime,
      closeSubmissionDate,
      state = {},
      visibility,
      title: courseTitle,
    } = currentCourse;

    const { scheduleDate, scheduleTime } = state;

    const oldCourses = _.get(assignmentData, "courses", []);
    const oldCourseData = _.find(oldCourses, { id: currentCourse.id }) || {};
    const {
      state: {
        scheduleDate: oldScheduleDate,
        scheduleTime: oldScheduleTime,
      } = {},
    } = oldCourseData;

    const opensOn = getMomentFromDateAndTimeString({
      dateString: scheduleDate,
      timeString: scheduleTime,
    });
    const dueOn = getMomentFromDateAndTimeString({
      dateString: deadlineDate,
      timeString: deadlineTime,
    });
    const closesOn = getMomentFromDateAndTimeString({
      dateTimeString: closeSubmissionDate,
    });
    const previousOpensOn = getMomentFromDateAndTimeString({
      dateString: oldScheduleDate,
      timeString: oldScheduleTime,
    });

    const dueOnToConsider = getDueOnBasedOnTimelineSettings({
      dueOn,
      currentTimelineSettings,
    });
    const isDueDateInPast = checkIsDateBefore({
      date1: dueOnToConsider,
      date2: moment(),
      key: "minutes",
    });
    const opensOnToConsider = getOpensOnBasedOnTimelineSettings({
      opensOn,
      previousOpensOn,
      currentTimelineSettings,
      isDueDateInPast,
    });
    const closesOnToConsider = getClosesOnBasedOnTimelineSettings({
      closesOn,
      currentTimelineSettings,
    });

    const isDueDateBeforeOpenDate = checkIsDueDateBeforeOpenDate({
      dueOn: dueOnToConsider,
      opensOn: opensOnToConsider,
      isDraftMode,
    });
    const isCloseDateBeforeDueDate = checkIsCloseDateBeforeDueDate({
      closesOn: closesOnToConsider,
      dueOn: dueOnToConsider,
    });
    const isCloseDateBeforeOpenDate = checkIsCloseDateBeforeOpenDate({
      closesOn: closesOnToConsider,
      opensOn: opensOnToConsider,
      isDraftMode,
    });

    const footerTextContent = getClassSettingsFooterText({
      visibility,
      opensOn: opensOnToConsider,
      dueOn: dueOnToConsider,
      closesOn: closesOnToConsider,
      isDueDateBeforeOpenDate,
      isCloseDateBeforeDueDate,
      isCloseDateBeforeOpenDate,
      t,
      isFixedStartTime,
      timedTestDuration,
    });

    // Format the text with course title prefix
    return `${courseTitle} : ${footerTextContent}`;
  }, [
    currentCourse,
    t,
    isFixedStartTime,
    timedTestDuration,
    currentTimelineSettings,
    assignmentData,
    isDraftMode,
  ]);

  // Navigation handlers
  const handlePreviousClass = () => {
    setCurrentClassIndex(prev => (prev > 0 ? prev - 1 : _.size(courses) - 1));
  };

  const handleNextClass = () => {
    setCurrentClassIndex(prev => (prev < _.size(courses) - 1 ? prev + 1 : 0));
  };

  // Don't render navigation arrows if only one course
  const showNavigation = _.size(courses) > 1;

  // Don't render if no courses
  if (_.isEmpty(courses)) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className="text-body-s">{footerText}</div>

      {showNavigation ? (
        <div className={classes.navigation}>
          <IconButton
            dsVersion="2.0"
            size="small"
            icon={<ChevronLeftOutlined />}
            variant="neutral"
            type="plain"
            onClick={handlePreviousClass}
          />
          <IconButton
            dsVersion="2.0"
            size="small"
            icon={<ChevronRightOutlined />}
            variant="neutral"
            type="plain"
            onClick={handleNextClass}
          />
        </div>
      ) : null}
    </div>
  );
};

export default AssignClassSettingsDataGridFooter;
